import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './SubtitleStyle.module.scss';
import placeholder from '../../../../assets/placeholderTN.png';
import SelectOption from '../../../../components/SelectOption/SelectOption';
import Slider from 'rc-slider';
import Icon from '../../../../components/Icon/Icon';
import cx from 'classnames';
import ColorPicker from '../../../../components/ColorPicker/ColorPicker';
import { CustomButton } from '../../../../components/Button/Button';
import { ToastNotification } from '../../../../components/Toast/Toast';
import Toast from '../../../../../common/utils/Toast';
import { useTranslation } from 'react-i18next';
import VideoService from '../../../../service/VideoService';
import { useUserContext } from '../../../../context/UserContext';
import Spinner from '../../../../../common/components/Spinner';
import {
  BASE_FONT_SIZE,
  BASE_PLAYER_HEIGHT,
  DEFAULT_SUBTITLE_SETTINGS,
  INIT_SUBTITLES_POSITION,
  MAX_WIDTH_SUBTITLES_LANDSCAPE,
} from '../../../../features/SubtitlesEditor/const';
import { checkFontLoaded, VIDEO_ORIENTATIONS } from '../../../../utils/SimulatedItemsUtility';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { BrandingAccordionHeader } from '../BrandingAccordionHeader/BrandingAccordionHeader';
import { useAccordion } from '../../hooks/useAccordion';
import { BrandingColorsSelection } from 'engage/components/BrandColorsSelection/BrandColorsSelection';

const styleOptions = {
  BACKGROUND: { name: 'company.settings.subtitle_style.text_background', type: 'background', isImage: true },
  SHADOW: { name: 'company.settings.subtitle_style.text_shadow', type: 'shadow', isImage: true },
  BORDER: { name: 'company.settings.subtitle_style.text_border', type: 'border', isImage: true },
  SIMPLE: { name: 'company.settings.subtitle_style.text_simple', type: 'simple', isImage: true },
};

const minPosition = 0;
const maxPosition = 100;

const MIN_FONT_SIZE = 20;
const MAX_FONT_SIZE = 50;

const MIN_BG_MARGIN = 0;

const initialSubtitlesSettings = {
  style: styleOptions.BACKGROUND,
  font: { name: 'Roboto' },
  fontSize: BASE_FONT_SIZE,
  textColor: '#FFFFFF',
  textOutlineColor: null,
  backgroundColor: '#000000bf',
  backgroundMargin: MIN_BG_MARGIN,
  textShadow: false,
  marginYLandscape: INIT_SUBTITLES_POSITION,
  marginYPortrait: INIT_SUBTITLES_POSITION,
  marginYSquare: INIT_SUBTITLES_POSITION,
};

const SubtitleStyle = ({ companyId, colors }) => {
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const { isActive, toggleAccordionState, contentRef } = useAccordion(true);

  const { userCompanyId, updateUserAppConfig, fonts, isSuperAdmin } = useUserContext();

  const [subtitlesSettings, setSubtitlesSettings] = useState(initialSubtitlesSettings);
  const [previouslySubtitlesSettings, setPreviouslySubtitlesSettings] = useState(initialSubtitlesSettings);
  const [defaultSubtitlesSettings, setDefaultSubtitlesSettings] = useState([]);

  const [isSaving, setIsSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  const [linesArrayLandscape, setLinesArrayLandscape] = useState([]);
  const [linesArrayPortrait, setLinesArrayPortrait] = useState([]);
  const [linesArraySquare, setLinesArraySquare] = useState([]);

  const [subtitlePositionLandscape, setSubtitlePositionsLandscape] = useState(0);
  const [subtitlePositionPortrait, setSubtitlePositionsPortrait] = useState(0);
  const [subtitlePositionSquare, setSubtitlePositionsSquare] = useState(0);

  const [isAdvancedOpened, setIsAdvancedOpened] = useState(false);

  const landscapeSubtitleRef = useRef(null);
  const portraitSubtitleRef = useRef(null);
  const squareSubtitleRef = useRef(null);

  const exampleLandscapeSubtitleRef = useRef(null);
  const examplePortraitSubtitleRef = useRef(null);
  const exampleSquareSubtitleRef = useRef(null);

  const history = useHistory();

  const [availableFonts, setAvailableFonts] = useState([]);

  const [areSubtitlesSettingsLoading, setAreSubtitlesSettingsLoading] = useState(false);
  const [areFontsLoading, setAreFontsLoading] = useState(false);
  const isLoading = useMemo(() => areSubtitlesSettingsLoading && areFontsLoading, [areSubtitlesSettingsLoading, areFontsLoading]);
  const id = useMemo(() => companyId ?? userCompanyId, [companyId, userCompanyId]);

  const currentlySelectedFont = useMemo(() => {
    return availableFonts.find(font => font.id === subtitlesSettings.font.id);
  }, [availableFonts, subtitlesSettings.font.id]);

  const { t, i18n } = useTranslation();

  const handleToggleAdvancedOpened = () => {
    setIsAdvancedOpened(prevState => !prevState);
  };

  const changeSubtitlesStyle = ({ style }) => {
    const changes = { style };

    // Change to default values only if there are empty values
    if (style.type === styleOptions.BACKGROUND.type) {
      if (!subtitlesSettings.backgroundColor) {
        const { backgroundColor } = defaultSubtitlesSettings?.find(({ backgroundColor }) => backgroundColor) || {};
        if (backgroundColor) {
          changes.backgroundColor = backgroundColor;
        }
      }
    }

    if (style.type === styleOptions.SHADOW.type) {
      if (!subtitlesSettings.textShadow) {
        const { textShadow } = defaultSubtitlesSettings?.find(({ textShadow }) => textShadow) || {};
        if (textShadow) {
          changes.textShadow = textShadow;
        }
      }
    }

    if (style.type === styleOptions.BORDER.type) {
      if (!subtitlesSettings.textOutlineColor) {
        const { textOutlineColor } = defaultSubtitlesSettings?.find(({ textOutlineColor }) => textOutlineColor) || {};
        if (textOutlineColor) {
          changes.textOutlineColor = textOutlineColor;
        }
      }
    }

    setSubtitlesSettings(prevState => ({ ...prevState, ...changes }));
    setIsChanged(true);
  };

  const changeSubtitlesSettings = (changes, registerChanges = true) => {
    setSubtitlesSettings(prevState => ({ ...prevState, ...changes }));
    setIsChanged(registerChanges);
  };

  const handleCancel = () => {
    changeSubtitlesSettings(previouslySubtitlesSettings, false);
  };

  const handleSave = async () => {
    try {
      const {
        font,
        fontSize,
        textColor,
        textOutlineColor,
        backgroundColor,
        marginYLandscape,
        marginYPortrait,
        marginYSquare,
        style,
        backgroundMargin,
      } = subtitlesSettings;

      const currentFont = availableFonts?.find(item => font.id === item.id);

      const payload = {
        ...(currentFont && { fontId: currentFont.id }),
        fontSize,
        textColor,
        marginYLandscape,
        marginYPortrait,
        marginYSquare,
      };

      // Set a value only to the field whose type is selected
      // Reset the rest to null
      if (style.type === styleOptions.BACKGROUND.type) {
        payload.backgroundColor = backgroundColor;
        payload.textOutlineColor = null;
        payload.textShadow = false;
      }

      if (style.type === styleOptions.SHADOW.type) {
        payload.backgroundColor = null;
        payload.textOutlineColor = null;
        payload.textShadow = true;
      }

      if (style.type === styleOptions.BORDER.type) {
        payload.backgroundColor = null;
        payload.textOutlineColor = textOutlineColor;
        payload.textShadow = false;
      }

      if (style.type === styleOptions.SIMPLE.type) {
        payload.backgroundColor = null;
        payload.textOutlineColor = null;
        payload.textShadow = false;
      }

      setIsSaving(true);

      const result = await VideoService.updateSubtitlesSettings(companyId ?? userCompanyId, payload);

      await updateUserAppConfig(result);

      if (result) {
        changeSubtitlesSettings(result, false);
        setPreviouslySubtitlesSettings(result);
      }

      setIsChanged(false);
      ToastNotification({
        message: t('account.settings.profile.changes_successfully'),
        iconName: 'success',
        isMobileView: isMobileView,
      });
    } catch (e) {
      Toast.error(t('clip_organizer.toast.error_while_saving'), e);
    } finally {
      setIsSaving(false);
    }
  };

  const defineLinesArrays = (parentRef, orientationType, fontCoeff) => {
    if (!parentRef.current?.offsetWidth) {
      setTimeout(() => defineLinesArrays(parentRef, orientationType, fontCoeff), 100);
      return;
    }

    const font = ((parentRef.current?.offsetHeight * subtitlesSettings.fontSize) / BASE_PLAYER_HEIGHT) * fontCoeff;

    const maxSubtitlesWidth =
      parentRef.current?.offsetWidth * (orientationType === VIDEO_ORIENTATIONS.landscape ? MAX_WIDTH_SUBTITLES_LANDSCAPE : 1);
    const maxSymbolsInString = maxSubtitlesWidth / font;

    const textArray = t('company.settings.subtitle_style.example_subtitle').split(/\s+/);

    const lines = [];
    let line = [];

    textArray.forEach(item => {
      if (item.length > 0) {
        const lineLength =
          line.length > 0
            ? line.length === 1
              ? line[0].length
              : line.map(item => item.length).reduce((previousValue, currentValue) => previousValue + currentValue)
            : 0;
        if (item.length + lineLength <= maxSymbolsInString) {
          line.push(item);
        } else {
          lines.push(line);
          line = [];
          if (item.length > 100) {
            let i = 0;
            let length = 0;
            while (i < item.length) {
              if (i + maxSymbolsInString < item.length) {
                length = i + maxSymbolsInString;
              } else {
                length = item.length - i;
              }
              line.push(item.slice(i, i + maxSymbolsInString));
              lines.push(line);
              line = [];
              i += maxSymbolsInString;
            }
          } else {
            line.push(item);
          }
        }
      }
    });

    if (line.length > 0) {
      lines.push(line);
      line = [];
    }

    if (orientationType === VIDEO_ORIENTATIONS.landscape) {
      setLinesArrayLandscape(lines.map(item => item?.join(' ')));
    }

    if (orientationType === VIDEO_ORIENTATIONS.portrait) {
      setLinesArrayPortrait(lines.map(item => item?.join(' ')));
    }

    if (orientationType === VIDEO_ORIENTATIONS.square) {
      setLinesArraySquare(lines.map(item => item?.join(' ')));
    }
  };

  const defineSubtitlePosition = (parentRef, currentRef, orientationType) => {
    if (!parentRef.current?.clientHeight || !currentRef.current?.clientHeight) {
      setTimeout(() => defineSubtitlePosition(parentRef, currentRef, orientationType), 100);
      return;
    }

    const calculatePosition = marginY => ((parentRef.current?.clientHeight - currentRef.current?.clientHeight) * marginY) / 100 || 0;

    if (orientationType === VIDEO_ORIENTATIONS.landscape) {
      setSubtitlePositionsLandscape(calculatePosition(subtitlesSettings.marginYLandscape));
    }

    if (orientationType === VIDEO_ORIENTATIONS.portrait) {
      setSubtitlePositionsPortrait(calculatePosition(subtitlesSettings.marginYPortrait));
    }

    if (orientationType === VIDEO_ORIENTATIONS.square) {
      setSubtitlePositionsSquare(calculatePosition(subtitlesSettings.marginYSquare));
    }
  };

  const customStyles = (parentRef, fontCoeff) => {
    // const initialBackgroundMargin = 5;
    if (!parentRef.current?.offsetWidth) {
      return {};
    }

    const borderSize = subtitlesSettings.fontSize > MAX_FONT_SIZE / 2 ? 0.5 : 0.25; // pixels
    const fontSize = ((parentRef.current?.offsetHeight * subtitlesSettings.fontSize) / BASE_PLAYER_HEIGHT) * fontCoeff;
    const styles = {
      fontFamily: `${currentlySelectedFont ? `'${currentlySelectedFont.name}', ` : ''}sans-serif`,
      fontSize: `${fontSize}px`,
      color: subtitlesSettings.textColor,
    };

    if (subtitlesSettings.style.type === styleOptions.BACKGROUND.type) {
      styles.background = subtitlesSettings.backgroundColor;
      // styles.padding = `${subtitlesSettings.backgroundMargin}px`;
    }

    if (subtitlesSettings.style.type === styleOptions.BORDER.type) {
      styles.webkitTextStroke = `${borderSize}px ${subtitlesSettings.textOutlineColor}`;
      styles.paintOrder = 'stroke fill';
    }

    if (subtitlesSettings.style.type === styleOptions.SHADOW.type) {
      styles.textShadow = '0px 2px 4px rgba(0, 0, 0, 0.75)';
    }

    return styles;
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      setAvailableFonts(fonts);
    }
  }, [isSuperAdmin, fonts]);

  useEffect(() => {
    let ignore = false;

    async function fetchFonts() {
      if (isSuperAdmin) {
        setAreFontsLoading(true);
        try {
          const newFonts = await VideoService.getFontsList(id);
          if (!ignore) {
            setAvailableFonts(newFonts);
            setAreFontsLoading(false);
          }
        } catch (e) {
          console.error(e);
        }
      }
    }

    if (id) {
      fetchFonts();
    }
    return () => {
      ignore = true;
    };
  }, [id, isSuperAdmin]);

  useEffect(() => {
    const getCustomSubtitlesSettings = async () => {
      setAreSubtitlesSettingsLoading(true);
      try {
        let settings = null;
        try {
          settings = await VideoService.getSubtitlesSettings(id);
        } catch (e) {
          console.error(e);
        }
        let defaultSettingsArray = [];
        try {
          defaultSettingsArray = await VideoService.getDefaultSubtitlesSettings();
        } catch (e) {
          console.error(e);
          const { fontId, ...fallbackSettings } = DEFAULT_SUBTITLE_SETTINGS;
          defaultSettingsArray = [
            {
              ...fallbackSettings,
              font: availableFonts.find(font => font.id === fontId),
            },
          ];
        }
        setDefaultSubtitlesSettings(defaultSettingsArray);

        // Set custom settings, otherwise set default settings from backend
        if (settings?.font) {
          if (settings.backgroundColor) {
            settings.style = styleOptions.BACKGROUND;
          } else if (settings.textShadow) {
            settings.style = styleOptions.SHADOW;
          } else if (settings.textOutlineColor) {
            settings.style = styleOptions.BORDER;
          } else {
            settings.style = styleOptions.SIMPLE;
          }

          changeSubtitlesSettings(settings, false);
          setPreviouslySubtitlesSettings(settings);
        } else {
          // Get values for text with background
          const textWithBackgroundSettings = defaultSettingsArray?.find(({ backgroundColor }) => backgroundColor);

          // Get values for text with shadow
          const textWithShadowSettings = defaultSettingsArray?.find(({ textShadow }) => textShadow);

          // Get value for text with border
          const textWithBorderSettings = defaultSettingsArray?.find(({ textOutlineColor }) => textOutlineColor);

          // Get value for simple text
          const simpleTextSettings = defaultSettingsArray?.find(
            ({ backgroundColor, textShadow, textOutlineColor }) => !backgroundColor && !textShadow && !textOutlineColor
          );

          if (textWithBackgroundSettings) {
            changeSubtitlesSettings({ ...textWithBackgroundSettings, style: styleOptions.BACKGROUND }, false);
            setPreviouslySubtitlesSettings(textWithBackgroundSettings);
          } else if (textWithShadowSettings) {
            changeSubtitlesSettings({ ...textWithShadowSettings, style: styleOptions.SHADOW }, false);
            setPreviouslySubtitlesSettings(textWithShadowSettings);
          } else if (textWithBorderSettings) {
            changeSubtitlesSettings({ ...textWithBorderSettings, style: styleOptions.BORDER }, false);
            setPreviouslySubtitlesSettings(textWithBorderSettings);
          } else if (simpleTextSettings) {
            changeSubtitlesSettings({ ...simpleTextSettings, style: styleOptions.SIMPLE }, false);
            setPreviouslySubtitlesSettings(simpleTextSettings);
          }
        }
      } catch (err) {
        console.log(err);
      }
      setAreSubtitlesSettingsLoading(false);
    };

    if (id) {
      getCustomSubtitlesSettings();
    }
  }, [availableFonts, id]);

  useEffect(() => {
    availableFonts?.forEach(async item => {
      try {
        checkFontLoaded(item);
      } catch (err) {
        console.log(err);
      }
    });
  }, [availableFonts]);

  useEffect(() => {
    defineLinesArrays(landscapeSubtitleRef, VIDEO_ORIENTATIONS.landscape, 1);
  }, [landscapeSubtitleRef.current?.offsetWidth, subtitlesSettings.fontSize, i18n.language]);

  useEffect(() => {
    defineLinesArrays(landscapeSubtitleRef, VIDEO_ORIENTATIONS.portrait, 0.75);
  }, [landscapeSubtitleRef.current?.offsetWidth, subtitlesSettings.fontSize, i18n.language]);

  useEffect(() => {
    defineLinesArrays(landscapeSubtitleRef, VIDEO_ORIENTATIONS.square, 0.75);
  }, [landscapeSubtitleRef?.current?.offsetWidth, subtitlesSettings.fontSize, i18n.language]);

  useEffect(() => {
    defineSubtitlePosition(landscapeSubtitleRef, exampleLandscapeSubtitleRef, VIDEO_ORIENTATIONS.landscape);
  }, [
    landscapeSubtitleRef.current?.clientHeight,
    exampleLandscapeSubtitleRef.current?.clientHeight,
    subtitlesSettings.marginYLandscape,
    linesArrayLandscape,
    linesArrayPortrait,
    linesArraySquare,
  ]);

  useEffect(() => {
    defineSubtitlePosition(portraitSubtitleRef, examplePortraitSubtitleRef, VIDEO_ORIENTATIONS.portrait);
  }, [
    portraitSubtitleRef.current?.clientHeight,
    examplePortraitSubtitleRef.current?.clientHeight,
    subtitlesSettings.marginYPortrait,
    linesArrayLandscape,
    linesArrayPortrait,
    linesArraySquare,
  ]);

  useEffect(() => {
    defineSubtitlePosition(squareSubtitleRef, exampleSquareSubtitleRef, VIDEO_ORIENTATIONS.square);
  }, [
    squareSubtitleRef.current?.clientHeight,
    exampleSquareSubtitleRef.current?.clientHeight,
    subtitlesSettings.marginYSquare,
    linesArrayLandscape,
    linesArrayPortrait,
    linesArraySquare,
  ]);

  useEffect(() => {
    const unblock = isChanged
      ? history.block(() => {
        // Navigation was blocked! Let's show a confirmation dialog
        // so the user can decide if they actually want to navigate
        // away and discard changes they've made in the current page.
        return confirm(t('clip_organizer.confirm_leave_page'));
      })
      : history.block(() => true);

    return () => unblock();
  }, [isChanged]);

  return (
    <div className={styles.wrapper}>
      <BrandingAccordionHeader isActive={isActive} onClick={toggleAccordionState} title={`${t('company.settings.subtitle_style.title')}`} description={t('company_settings.branding.subtitles_style_description')} />
      <div ref={contentRef} style={{ overflow: 'hidden' }}>
        <div className={styles.subtitleStyleWrapper}>
          <div className={styles.orientationsImageContainer}>
            <div ref={landscapeSubtitleRef} className={styles.landscapeImageContainer}>
              <img src={placeholder} className={styles.exampleImage} alt={`${t('storyboard.generating.orientation.landscape')} ${t('company.settings.logos.update.preview')}`} />
              <div
                ref={exampleLandscapeSubtitleRef}
                className={cx(styles.subtitleWrapper)}
                style={{ bottom: `${subtitlePositionLandscape}px` }}
              >
                {linesArrayLandscape.map((item, index) => (
                  <span key={index} className={styles.subtitleTextWrapper} style={customStyles(landscapeSubtitleRef, 1)}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div ref={portraitSubtitleRef} className={styles.portraitImageContainer}>
              <img src={placeholder} className={styles.exampleImage} alt={`${t('storyboard.generating.orientation.portrait')} ${t('company.settings.logos.update.preview')}`} />
              <div
                ref={examplePortraitSubtitleRef}
                className={cx(styles.subtitleWrapper)}
                style={{ bottom: `${subtitlePositionPortrait}px` }}
              >
                {linesArrayPortrait.map((item, index) => (
                  <span key={index} className={styles.subtitleTextWrapper} style={customStyles(landscapeSubtitleRef, 0.75)}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div ref={squareSubtitleRef} className={styles.squareImageContainer}>
              <img src={placeholder} className={styles.exampleImage} alt={`${t('storyboard.generating.orientation.square')} ${t('company.settings.logos.update.preview')}`} />
              <div ref={exampleSquareSubtitleRef} className={cx(styles.subtitleWrapper)} style={{ bottom: `${subtitlePositionSquare}px` }}>
                {linesArraySquare.map((item, index) => (
                  <span key={index} className={styles.subtitleTextWrapper} style={customStyles(landscapeSubtitleRef, 0.75)}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.subtitleOptionContainer}>
            <SelectOption
              title={t('company.settings.subtitle_style.style')}
              currentlySelected={subtitlesSettings.style}
              options={Object.values(styleOptions)}
              onSelect={style => changeSubtitlesStyle({ style })}
            />
            <div>
              <span className={styles.sliderTitle}>{t('company.settings.subtitle_style.position')}</span>
              <div className={styles.sliderWrapper}>
                <div className={styles.sliderContainer}>
                  <Icon src="landscapeStoryboard" className={styles.orientationIcon} />
                  <span className={styles.minMaxValue}>{minPosition}</span>
                  <Slider
                    min={minPosition}
                    max={maxPosition}
                    step={1}
                    onChange={value =>
                      changeSubtitlesSettings({
                        marginYLandscape: Math.round(value),
                      })
                    }
                    value={subtitlesSettings.marginYLandscape}
                    included
                    style={{ width: '175px' }}
                    handleStyle={{
                      width: '18px',
                      height: '18px',
                      background: '#FFFFFF',
                      position: 'relative',
                      top: '-10px',
                      borderRadius: '9px',
                      boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
                      cursor: 'pointer',
                    }}
                    trackStyle={{
                      background: '#37C1A0',
                      height: '4px',
                      borderRadius: '2px',
                      position: 'relative',
                      top: '-4px',
                    }}
                    railStyle={{
                      background: '#EEEEEE',
                      height: '4px',
                      borderRadius: '2px',
                      position: 'relative',
                    }}
                  />
                  <span className={styles.minMaxValue}>{maxPosition}</span>
                  <div className={styles.currentValueContainer}>
                    <div
                      className={styles.currentValue}
                      style={{
                        left: `calc(${((subtitlesSettings.marginYLandscape - minPosition) / (maxPosition - minPosition)) *
                          100}% + 11.5px)`,
                      }}
                    >
                      {subtitlesSettings.marginYLandscape}
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.sliderWrapper}>
                <div className={styles.sliderContainer}>
                  <Icon src="portraitStoryboard" className={styles.orientationIcon} />
                  <span className={styles.minMaxValue}>{minPosition}</span>
                  <Slider
                    min={minPosition}
                    max={maxPosition}
                    step={1}
                    onChange={value =>
                      changeSubtitlesSettings({
                        marginYPortrait: Math.round(value),
                      })
                    }
                    value={subtitlesSettings.marginYPortrait}
                    included
                    style={{ width: '175px' }}
                    handleStyle={{
                      width: '18px',
                      height: '18px',
                      background: '#FFFFFF',
                      position: 'relative',
                      top: '-10px',
                      borderRadius: '9px',
                      boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
                      cursor: 'pointer',
                    }}
                    trackStyle={{
                      background: '#37C1A0',
                      height: '4px',
                      borderRadius: '2px',
                      position: 'relative',
                      top: '-4px',
                    }}
                    railStyle={{
                      background: '#EEEEEE',
                      height: '4px',
                      borderRadius: '2px',
                      position: 'relative',
                    }}
                  />
                  <span className={styles.minMaxValue}>{maxPosition}</span>
                  <div className={styles.currentValueContainer}>
                    <div
                      className={styles.currentValue}
                      style={{
                        left: `calc(${((subtitlesSettings.marginYPortrait - minPosition) / (maxPosition - minPosition)) * 100}% + 11.5px)`,
                      }}
                    >
                      {subtitlesSettings.marginYPortrait}
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.sliderWrapper}>
                <div className={styles.sliderContainer}>
                  <Icon src="squareStoryboard" className={styles.orientationIcon} />
                  <span className={styles.minMaxValue}>{minPosition}</span>
                  <Slider
                    min={minPosition}
                    max={maxPosition}
                    step={1}
                    onChange={value =>
                      changeSubtitlesSettings({
                        marginYSquare: Math.round(value),
                      })
                    }
                    value={subtitlesSettings.marginYSquare}
                    included
                    style={{ width: '175px' }}
                    handleStyle={{
                      width: '18px',
                      height: '18px',
                      background: '#FFFFFF',
                      position: 'relative',
                      top: '-10px',
                      borderRadius: '9px',
                      boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
                      cursor: 'pointer',
                    }}
                    trackStyle={{
                      background: '#37C1A0',
                      height: '4px',
                      borderRadius: '2px',
                      position: 'relative',
                      top: '-4px',
                    }}
                    railStyle={{
                      background: '#EEEEEE',
                      height: '4px',
                      borderRadius: '2px',
                      position: 'relative',
                    }}
                  />
                  <span className={styles.minMaxValue}>{maxPosition}</span>
                  <div className={styles.currentValueContainer}>
                    <div
                      className={styles.currentValue}
                      style={{
                        left: `calc(${((subtitlesSettings.marginYSquare - minPosition) / (maxPosition - minPosition)) * 100}% + 11.5px)`,
                      }}
                    >
                      {subtitlesSettings.marginYSquare}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.advancedOptionsContainer} onClick={handleToggleAdvancedOpened}>
            <span>
              {isAdvancedOpened
                ? t('company.settings.subtitle_style.hide_advanced_settings')
                : t('company.settings.subtitle_style.show_advanced_settings')}
            </span>
            <Icon src="arrowDown" alt="" className={cx(styles.arrowButton, { [styles.turnArrow]: isAdvancedOpened })} />
          </div>
          {isAdvancedOpened && (
            <>
              <div className={styles.subtitleOptionContainer}>
                <SelectOption
                  title={t('company.settings.subtitle_style.font')}
                  currentlySelected={currentlySelectedFont}
                  options={availableFonts}
                  onSelect={font => changeSubtitlesSettings({ font })}
                />
                <div className={styles.sliderWrapper}>
                  <span className={styles.sliderTitle}>{t('company.settings.subtitle_style.font_size')}</span>
                  <div className={styles.sliderContainer}>
                    <span className={styles.minMaxValue}>{MIN_FONT_SIZE}</span>
                    <Slider
                      min={MIN_FONT_SIZE}
                      max={MAX_FONT_SIZE}
                      step={1}
                      onChange={value => changeSubtitlesSettings({ fontSize: Math.round(value) })}
                      value={subtitlesSettings.fontSize}
                      included
                      style={{ width: '175px' }}
                      handleStyle={{
                        width: '18px',
                        height: '18px',
                        background: '#FFFFFF',
                        position: 'relative',
                        top: '-10px',
                        borderRadius: '9px',
                        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
                        cursor: 'pointer',
                      }}
                      trackStyle={{
                        background: '#37C1A0',
                        height: '4px',
                        borderRadius: '2px',
                        position: 'relative',
                        top: '-4px',
                      }}
                      railStyle={{
                        background: '#EEEEEE',
                        height: '4px',
                        borderRadius: '2px',
                        position: 'relative',
                      }}
                    />
                    <span className={styles.minMaxValue}>{MAX_FONT_SIZE}</span>
                    <div className={styles.currentValueContainer}>
                      <div
                        className={styles.currentValue}
                        style={{
                          left: `calc(${((subtitlesSettings.fontSize - MIN_FONT_SIZE) / (MAX_FONT_SIZE - MIN_FONT_SIZE)) *
                            100}% + 19.5px)`,
                        }}
                      >
                        {subtitlesSettings.fontSize}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.subtitleOptionContainer}>
                <div className={styles.colorWrapper}>

                  <span className={styles.title}>
                    {t('company.settings.subtitle_style.font_color')}
                  </span>
                  <BrandingColorsSelection className={styles.colorPicker} allowCustom={true} active={subtitlesSettings.textColor ?? '#FFFFFF00'} colors={colors} additionalColors={['#000000', '#FFFFFF']} onSelect={color => changeSubtitlesSettings({ textColor: color })} />
                </div>
                {subtitlesSettings.style.type === styleOptions.BORDER.type && (
                  <div className={styles.colorWrapper}>
                    <span className={styles.title}>
                      {t('company.settings.subtitle_style.border_color')}
                    </span>
                    <BrandingColorsSelection className={styles.colorPicker} allowCustom={true} active={subtitlesSettings.textOutlineColor ?? '#FFFFFF00'} colors={colors} additionalColors={['#000000']} onSelect={color => changeSubtitlesSettings({ textOutlineColor: color })} />
                  </div>
                )}
                {subtitlesSettings.style.type === styleOptions.BACKGROUND.type && (
                  <div className={styles.colorWrapper}>
                    <span className={styles.title}>
                      {t('company.settings.subtitle_style.background_color')}
                    </span>
                    <BrandingColorsSelection className={styles.colorPicker} allowCustom={true} active={subtitlesSettings.backgroundColor ?? '#FFFFFF00'} colors={colors} additionalColors={['#000000']} onSelect={color => changeSubtitlesSettings({ backgroundColor: color })} />
                  </div>
                )}
              </div>
            </>
          )}
          <div className={styles.saveBtnContainer}>
            <CustomButton
              disabled={isSaving || !isChanged}
              label={t('general.save')}
              variant="primary"
              onClick={handleSave}
              className={styles.cancelButton}
            />
            {isChanged && (
              <CustomButton
                disabled={isSaving}
                label={t('general.cancel')}
                variant="secondary"
                onClick={handleCancel}
                className={styles.cancelButton}
              />
            )}
          </div>
        </div>
      </div>
      {isLoading && isActive && <Spinner width="40px" height="40px" size="4px" className={styles.spinner} />}
    </div>
  );
};

export default SubtitleStyle;
